/**
 * tableFormula — spreadsheet-style formula evaluation for Markdown tables.
 *
 * MarkdownView renders GFM tables as static text, so a cell whose text is
 * `=E2-F2` shows literally. This module turns those cells into COMPUTED values
 * (like a spreadsheet), so a sprint-planning capacity table calculates.
 *
 * INPUT is exactly what MarkdownView already computes via its `splitRow`:
 *   - headerCells: string[]        (the table's header row)
 *   - bodyRows:    string[][]      (each data row's cells)
 * OUTPUT is a computed display grid where any cell whose trimmed text starts
 * with '=' is evaluated to its computed value; every non-formula cell passes
 * through UNCHANGED (so MarkdownView's inline renderer still handles the bold /
 * links inside it).
 *
 * ADDRESSING is A1-style. Column letters map A->col0, B->col1, ... with
 * multi-letter support (AA, AB, ...). Row numbers are 1-based over the WHOLE
 * table: row 1 is the HEADER row and row 2 is the FIRST body row, so `E2` is
 * column E of the first data row. A reference resolves to that cell's value; if
 * the referenced cell is itself a formula it is evaluated RECURSIVELY (results
 * are memoized, and reference cycles yield the error value `#CYCLE`).
 *
 * GRAMMAR (lowest to highest precedence):
 *   comparison  =  <>  <  >  <=  >=          (returns a boolean)
 *   additive    +  -
 *   multiplic.  *  /
 *   unary       -  +
 *   primary     number | "string" | cellRef | ( expr ) | ROUND(v,d) | IF(c,t,e)
 * Empty/blank referenced cells evaluate to "" for string comparison and 0 in
 * arithmetic. Numeric results format cleanly (trailing zeros trimmed, e.g. 24,
 * 28.8, 25.9). ROUND rounds half-up (away from zero) to the given digits.
 *
 * SAFETY: evaluation is a hand-written tokenizer + recursive-descent parser
 * over this fixed grammar and a fixed two-function whitelist. It NEVER uses
 * `eval` or `new Function`, mirroring the estimation engine
 * (src/features/estimation/engine.ts). Any parse/eval failure is captured as
 * the visible string `#ERR` and never thrown out of {@link computeTableGrid}.
 *
 * Pure TS — no React, no I/O, no globals mutated.
 */

/** A value the formula grammar can produce or operate on. */
export type CellValue = number | string | boolean;

/** The result of computing a whole table. */
export interface ComputedGrid {
  /** Computed header row (formula cells evaluated; others unchanged). */
  header: string[];
  /** Computed body rows (formula cells evaluated; others unchanged). */
  body: string[][];
  /** True when at least one cell in the table was a formula. */
  hasFormula: boolean;
}

/** Sentinel display value for a reference cycle. */
const CYCLE = '#CYCLE';
/** Sentinel display value for any parse/eval error. */
const ERR = '#ERR';

/** Thrown internally when a reference cycle is detected. */
class CycleError extends Error {}

/** Thrown internally for any parse/eval problem (surfaced as `#ERR`). */
class FormulaError extends Error {}

// ---------------------------------------------------------------------------
// Addressing
// ---------------------------------------------------------------------------

/**
 * Converts a column label (A, B, ..., Z, AA, AB, ...) to a 0-based index.
 * Case-insensitive. Throws on a non-alphabetic label.
 */
export function columnLetterToIndex(letters: string): number {
  if (!letters || !/^[A-Za-z]+$/.test(letters)) {
    throw new FormulaError(`Invalid column "${letters}"`);
  }
  let index = 0;
  for (const ch of letters.toUpperCase()) {
    index = index * 26 + (ch.charCodeAt(0) - 64); // 'A' -> 1
  }
  return index - 1; // 0-based
}

/**
 * Parses an A1 cell reference (e.g. `E2`, `AA3`) into 0-based grid coordinates,
 * where rowIndex 0 is the header row (A1 style row 1). Throws on a bad ref or a
 * row number below 1.
 */
export function parseCellRef(ref: string): { rowIndex: number; colIndex: number } {
  const match = /^([A-Za-z]+)([0-9]+)$/.exec(ref);
  if (!match) throw new FormulaError(`Invalid cell reference "${ref}"`);
  const rowNumber = Number(match[2]);
  if (!Number.isInteger(rowNumber) || rowNumber < 1) {
    throw new FormulaError(`Invalid row in reference "${ref}"`);
  }
  return { rowIndex: rowNumber - 1, colIndex: columnLetterToIndex(match[1]) };
}

// ---------------------------------------------------------------------------
// Value helpers
// ---------------------------------------------------------------------------

/** Matches a plain integer/decimal number (optionally signed). */
const NUMERIC_RE = /^[-+]?(?:\d+\.?\d*|\.\d+)$/;

/** Interprets a raw (non-formula) cell's text as a number when it looks like one. */
function literalValue(raw: string): CellValue {
  const trimmed = raw.trim();
  if (trimmed === '') return '';
  if (NUMERIC_RE.test(trimmed)) {
    const n = Number(trimmed);
    if (Number.isFinite(n)) return n;
  }
  return trimmed;
}

/** Coerces a value to a number for arithmetic; blank -> 0, non-numeric -> throw. */
function toNum(v: CellValue): number {
  if (typeof v === 'number') return v;
  if (typeof v === 'boolean') return v ? 1 : 0;
  const s = v.trim();
  if (s === '') return 0;
  const n = Number(s);
  if (!Number.isFinite(n)) throw new FormulaError(`"${v}" is not a number`);
  return n;
}

/** Loose equality: same-type compare only; differing types are never equal. */
function equals(a: CellValue, b: CellValue): boolean {
  if (typeof a === 'number' && typeof b === 'number') return a === b;
  if (typeof a === 'string' && typeof b === 'string') return a === b;
  if (typeof a === 'boolean' && typeof b === 'boolean') return a === b;
  return false;
}

/** Rounds half-up (away from zero) to `digits`, correcting FP representation. */
function roundHalfUp(value: number, digits: number): number {
  if (!Number.isFinite(value)) throw new FormulaError('ROUND of non-finite value');
  const d = Math.trunc(digits);
  const factor = 10 ** d;
  // toPrecision(12) removes binary FP noise (e.g. 234.49999999999997 -> 234.5)
  // so genuine halves round up rather than down.
  const scaled = Number((value * factor).toPrecision(12));
  const rounded = Math.sign(scaled) * Math.round(Math.abs(scaled));
  return rounded / factor;
}

/**
 * Formats a computed number for display: strips FP noise and trailing zeros
 * (24, 28.8, 25.9). Non-numeric values format as their string / TRUE / FALSE.
 */
export function formatValue(v: CellValue): string {
  if (typeof v === 'boolean') return v ? 'TRUE' : 'FALSE';
  if (typeof v === 'string') return v;
  if (!Number.isFinite(v)) throw new FormulaError('non-finite result');
  const cleaned = Number(v.toPrecision(12));
  return String(cleaned);
}

/** True when a display string is a plain number (used for right-alignment). */
export function isNumericDisplay(display: string): boolean {
  return NUMERIC_RE.test(display.trim());
}

// ---------------------------------------------------------------------------
// Tokenizer
// ---------------------------------------------------------------------------

type TokenType = 'number' | 'string' | 'ref' | 'name' | 'op' | 'eof';

interface Token {
  type: TokenType;
  value: string;
  pos: number;
}

const MULTI_CHAR_OPS = ['<=', '>=', '<>'];
const SINGLE_CHAR_OPS = new Set(['+', '-', '*', '/', '(', ')', ',', '<', '>', '=', ':']);

/**
 * Coerces a list of cell values to the numbers among them, ignoring blanks,
 * booleans, and non-numeric text — matching spreadsheet aggregate behavior
 * (e.g. SUM ignores text/blank cells).
 */
function numbersOf(values: CellValue[]): number[] {
  const out: number[] = [];
  for (const v of values) {
    if (typeof v === 'number') {
      if (Number.isFinite(v)) out.push(v);
      continue;
    }
    if (typeof v === 'boolean') {
      continue;
    }
    const s = v.trim();
    if (s === '') {
      continue;
    }
    const n = Number(s);
    if (Number.isFinite(n)) {
      out.push(n);
    }
  }
  return out;
}

/**
 * Evaluates a SUMIF/COUNTIF criterion (e.g. {@code ">5"}, {@code "<>SDE Intern"},
 * {@code "SDE"}) against a cell value. Numeric comparisons apply when both sides
 * are numeric; otherwise equality/inequality is a case-insensitive string match.
 */
function matchesCriteria(value: CellValue, criteria: CellValue): boolean {
  const crit = (typeof criteria === 'string' ? criteria : String(criteria)).trim();
  const match = /^(<=|>=|<>|=|<|>)?\s*(.*)$/.exec(crit);
  const operator = match?.[1] ?? '=';
  const rightRaw = (match?.[2] ?? '').trim();
  const valueText = (typeof value === 'string' ? value : String(value)).trim();
  const rightNumber = Number(rightRaw);
  const valueNumber = Number(valueText);
  const bothNumeric =
    valueText !== '' && rightRaw !== '' && Number.isFinite(valueNumber) && Number.isFinite(rightNumber);
  switch (operator) {
    case '<>':
      return bothNumeric
        ? valueNumber !== rightNumber
        : valueText.toLowerCase() !== rightRaw.toLowerCase();
    case '>':
      return bothNumeric && valueNumber > rightNumber;
    case '<':
      return bothNumeric && valueNumber < rightNumber;
    case '>=':
      return bothNumeric && valueNumber >= rightNumber;
    case '<=':
      return bothNumeric && valueNumber <= rightNumber;
    default:
      return bothNumeric
        ? valueNumber === rightNumber
        : valueText.toLowerCase() === rightRaw.toLowerCase();
  }
}


/**
 * Normalizes a formula source string for common copy-paste artifacts before
 * tokenizing, so pasted spreadsheet/Markdown content still evaluates:
 * <ul>
 *   <li>Markdown-escaped punctuation (e.g. {@code \*}, {@code \-}, {@code \(}) —
 *       drops the escaping backslash so {@code =G2\*1.2} becomes {@code =G2*1.2}.</li>
 *   <li>Smart/curly quotes (“ ” ‘ ’) — folded to straight {@code "} / {@code '}
 *       so {@code IF(K2=“”,0,K2)} parses like {@code IF(K2="",0,K2)}.</li>
 *   <li>Non-breaking spaces — folded to a normal space.</li>
 * </ul>
 * Formulas never legitimately contain backslashes or curly quotes, so this is
 * a safe, loss-free normalization.
 *
 * @param body the formula body (the text after the leading {@code =})
 * @return the normalized formula body
 */
function normalizeFormulaSource(body: string): string {
  return body
    .replace(/[\u201C\u201D\u201E\u201F\u2033]/g, '"')
    .replace(/[\u2018\u2019\u201A\u201B\u2032]/g, "'")
    .replace(/\u00A0/g, ' ')
    .replace(/\\(?=[^0-9A-Za-z\s])/g, '');
}

function tokenize(input: string): Token[] {
  const tokens: Token[] = [];
  let i = 0;
  const n = input.length;

  while (i < n) {
    const ch = input[i];

    if (ch === ' ' || ch === '\t' || ch === '\n' || ch === '\r') {
      i++;
      continue;
    }

    // Number (int or decimal).
    if ((ch >= '0' && ch <= '9') || (ch === '.' && /[0-9]/.test(input[i + 1] ?? ''))) {
      let j = i + 1;
      while (j < n && /[0-9.]/.test(input[j])) j++;
      const value = input.slice(i, j);
      if ((value.match(/\./g) ?? []).length > 1) {
        throw new FormulaError(`Malformed number "${value}"`);
      }
      tokens.push({ type: 'number', value, pos: i });
      i = j;
      continue;
    }

    // Double-quoted string literal (supports "" and backslash escapes).
    if (ch === '"') {
      let j = i + 1;
      let str = '';
      while (j < n && input[j] !== '"') {
        if (input[j] === '\\' && j + 1 < n) {
          str += input[j + 1];
          j += 2;
        } else {
          str += input[j];
          j++;
        }
      }
      if (j >= n) throw new FormulaError('Unterminated string literal');
      tokens.push({ type: 'string', value: str, pos: i });
      i = j + 1;
      continue;
    }

    // Identifier: letters then optional digits. Letters+digits => cell ref
    // (E2, AA3); letters only => a function name (ROUND, IF).
    if (/[A-Za-z]/.test(ch)) {
      let j = i + 1;
      while (j < n && /[A-Za-z]/.test(input[j])) j++;
      const letterEnd = j;
      while (j < n && /[0-9]/.test(input[j])) j++;
      const hasDigits = j > letterEnd;
      tokens.push({
        type: hasDigits ? 'ref' : 'name',
        value: input.slice(i, j),
        pos: i,
      });
      i = j;
      continue;
    }

    const two = input.slice(i, i + 2);
    if (MULTI_CHAR_OPS.includes(two)) {
      tokens.push({ type: 'op', value: two, pos: i });
      i += 2;
      continue;
    }

    if (SINGLE_CHAR_OPS.has(ch)) {
      tokens.push({ type: 'op', value: ch, pos: i });
      i++;
      continue;
    }

    throw new FormulaError(`Unexpected character "${ch}"`);
  }

  tokens.push({ type: 'eof', value: '', pos: n });
  return tokens;
}

// ---------------------------------------------------------------------------
// Parser (recursive descent) + evaluator
// ---------------------------------------------------------------------------

/** Resolves a cell reference to its (recursively computed) value. */
type RefResolver = (ref: string) => CellValue;

class Parser {
  private pos = 0;

  constructor(
    private readonly tokens: Token[],
    private readonly resolve: RefResolver,
    private readonly resolveRange: (start: string, end: string) => CellValue[],
  ) {}

  private peek(): Token {
    return this.tokens[this.pos];
  }

  private next(): Token {
    return this.tokens[this.pos++];
  }

  private isOp(value: string): boolean {
    const t = this.peek();
    return t.type === 'op' && t.value === value;
  }

  private expect(value: string): void {
    if (!this.isOp(value)) {
      throw new FormulaError(`Expected "${value}" but found "${this.peek().value || 'end'}"`);
    }
    this.pos++;
  }

  /** Entry point: parse a full expression and assert nothing trails. */
  parse(): CellValue {
    const value = this.parseComparison();
    if (this.peek().type !== 'eof') {
      throw new FormulaError(`Unexpected trailing token "${this.peek().value}"`);
    }
    return value;
  }

  // comparison: additive ( (= <> < > <= >=) additive )*   -> boolean
  private parseComparison(): CellValue {
    let left = this.parseAdditive();
    while (
      this.isOp('=') ||
      this.isOp('<>') ||
      this.isOp('<') ||
      this.isOp('>') ||
      this.isOp('<=') ||
      this.isOp('>=')
    ) {
      const op = this.next().value;
      const right = this.parseAdditive();
      switch (op) {
        case '=':
          left = equals(left, right);
          break;
        case '<>':
          left = !equals(left, right);
          break;
        case '<':
          left = toNum(left) < toNum(right);
          break;
        case '>':
          left = toNum(left) > toNum(right);
          break;
        case '<=':
          left = toNum(left) <= toNum(right);
          break;
        default:
          left = toNum(left) >= toNum(right);
          break;
      }
    }
    return left;
  }

  private parseAdditive(): CellValue {
    let left = this.parseMultiplicative();
    while (this.isOp('+') || this.isOp('-')) {
      const op = this.next().value;
      const right = this.parseMultiplicative();
      left = op === '+' ? toNum(left) + toNum(right) : toNum(left) - toNum(right);
    }
    return left;
  }

  private parseMultiplicative(): CellValue {
    let left = this.parseUnary();
    while (this.isOp('*') || this.isOp('/')) {
      const op = this.next().value;
      const right = this.parseUnary();
      const l = toNum(left);
      const r = toNum(right);
      if (op === '/') {
        if (r === 0) throw new FormulaError('Division by zero');
        left = l / r;
      } else {
        left = l * r;
      }
    }
    return left;
  }

  private parseUnary(): CellValue {
    if (this.isOp('-')) {
      this.next();
      return -toNum(this.parseUnary());
    }
    if (this.isOp('+')) {
      this.next();
      return toNum(this.parseUnary());
    }
    return this.parsePrimary();
  }

  private parsePrimary(): CellValue {
    const t = this.peek();

    if (t.type === 'number') {
      this.next();
      return Number(t.value);
    }

    if (t.type === 'string') {
      this.next();
      return t.value;
    }

    if (t.type === 'ref') {
      this.next();
      return this.resolve(t.value);
    }

    if (this.isOp('(')) {
      this.next();
      const value = this.parseComparison();
      this.expect(')');
      return value;
    }

    if (t.type === 'name') {
      this.next();
      this.expect('(');
      const argLists: CellValue[][] = [];
      if (!this.isOp(')')) {
        argLists.push(this.parseArgument());
        while (this.isOp(',')) {
          this.next();
          argLists.push(this.parseArgument());
        }
      }
      this.expect(')');
      return this.callFunction(t.value.toUpperCase(), argLists);
    }

    throw new FormulaError(`Unexpected token "${t.value || 'end'}"`);
  }

  /**
   * Parses one function argument, returning the list of values it contributes:
   * a range {@code A1:B9} expands to all cells in that rectangle; any other
   * argument is a single-value list.
   */
  private parseArgument(): CellValue[] {
    const current = this.peek();
    const separator = this.tokens[this.pos + 1];
    const endRef = this.tokens[this.pos + 2];
    if (
      current.type === 'ref' &&
      separator && separator.type === 'op' && separator.value === ':' &&
      endRef && endRef.type === 'ref'
    ) {
      const start = this.next().value;
      this.next();
      const end = this.next().value;
      return this.resolveRange(start, end);
    }
    return [this.parseComparison()];
  }

  private callFunction(name: string, argLists: CellValue[][]): CellValue {
    const scalars = argLists.map((list) => (list.length > 0 ? list[0] : ''));
    const flat = argLists.flat();
    switch (name) {
      case 'ROUND': {
        if (scalars.length < 1 || scalars.length > 2) {
          throw new FormulaError('ROUND expects (value, digits)');
        }
        const digits = scalars.length === 2 ? toNum(scalars[1]) : 0;
        return roundHalfUp(toNum(scalars[0]), digits);
      }
      case 'IF': {
        if (scalars.length !== 3) throw new FormulaError('IF expects (condition, then, else)');
        return truthy(scalars[0]) ? scalars[1] : scalars[2];
      }
      case 'SUM':
        return numbersOf(flat).reduce((acc, x) => acc + x, 0);
      case 'AVERAGE': {
        const nums = numbersOf(flat);
        if (nums.length === 0) throw new FormulaError('AVERAGE of an empty range');
        return nums.reduce((acc, x) => acc + x, 0) / nums.length;
      }
      case 'MIN': {
        const nums = numbersOf(flat);
        if (nums.length === 0) throw new FormulaError('MIN of an empty range');
        return Math.min(...nums);
      }
      case 'MAX': {
        const nums = numbersOf(flat);
        if (nums.length === 0) throw new FormulaError('MAX of an empty range');
        return Math.max(...nums);
      }
      case 'COUNT':
        return numbersOf(flat).length;
      case 'SUMIF': {
        if (argLists.length < 2) throw new FormulaError('SUMIF expects (range, criteria, [sumRange])');
        const criteriaRange = argLists[0];
        const criterion = argLists[1][0] ?? '';
        const sumRange = argLists.length >= 3 ? argLists[2] : argLists[0];
        let total = 0;
        for (let i = 0; i < criteriaRange.length; i++) {
          if (matchesCriteria(criteriaRange[i], criterion)) {
            const cell = sumRange[i];
            if (typeof cell === 'number' && Number.isFinite(cell)) {
              total += cell;
            } else if (typeof cell === 'string' && cell.trim() !== '' && Number.isFinite(Number(cell))) {
              total += Number(cell);
            }
          }
        }
        return total;
      }
      case 'COUNTIF': {
        if (argLists.length < 2) throw new FormulaError('COUNTIF expects (range, criteria)');
        const criterion = argLists[1][0] ?? '';
        return argLists[0].filter((cell) => matchesCriteria(cell, criterion)).length;
      }
      default:
        throw new FormulaError(`Unknown function "${name}"`);
    }
  }
}

/** Truthiness for IF's condition: booleans as-is, 0/""/NaN falsy. */
function truthy(v: CellValue): boolean {
  if (typeof v === 'boolean') return v;
  if (typeof v === 'number') return v !== 0 && !Number.isNaN(v);
  return v !== '';
}

// ---------------------------------------------------------------------------
// Table evaluation
// ---------------------------------------------------------------------------

class TableEvaluator {
  /** Raw grid: rows[0] is the header row (A1 row 1). */
  private readonly rows: string[][];
  private readonly memo = new Map<string, CellValue>();
  private readonly visiting = new Set<string>();

  constructor(header: string[], body: string[][]) {
    this.rows = [header, ...body];
  }

  private rawCell(rowIndex: number, colIndex: number): string {
    if (rowIndex < 0 || colIndex < 0) return '';
    const row = this.rows[rowIndex];
    if (!row) return '';
    return row[colIndex] ?? '';
  }

  /** Resolves a cell (by grid coords) to its value, evaluating formulas. */
  private resolveCell(rowIndex: number, colIndex: number): CellValue {
    const key = `${rowIndex},${colIndex}`;
    const cached = this.memo.get(key);
    if (cached !== undefined) return cached;
    if (this.visiting.has(key)) throw new CycleError();

    const raw = this.rawCell(rowIndex, colIndex).trim();
    if (!raw.startsWith('=')) {
      const value = literalValue(raw);
      this.memo.set(key, value);
      return value;
    }

    this.visiting.add(key);
    let value: CellValue;
    try {
      value = this.evaluateFormula(raw.slice(1));
    } finally {
      this.visiting.delete(key);
    }
    this.memo.set(key, value);
    return value;
  }

  /** Tokenizes + parses a formula body, resolving refs back into the grid. */
  private evaluateFormula(body: string): CellValue {
    const resolver: RefResolver = (ref) => {
      const { rowIndex, colIndex } = parseCellRef(ref);
      return this.resolveCell(rowIndex, colIndex);
    };
    const rangeResolver = (start: string, end: string): CellValue[] => {
      const a = parseCellRef(start);
      const b = parseCellRef(end);
      const rowStart = Math.min(a.rowIndex, b.rowIndex);
      const rowEnd = Math.max(a.rowIndex, b.rowIndex);
      const colStart = Math.min(a.colIndex, b.colIndex);
      const colEnd = Math.max(a.colIndex, b.colIndex);
      const values: CellValue[] = [];
      for (let r = rowStart; r <= rowEnd; r++) {
        for (let c = colStart; c <= colEnd; c++) {
          values.push(this.resolveCell(r, c));
        }
      }
      return values;
    };
    return new Parser(tokenize(normalizeFormulaSource(body)), resolver, rangeResolver).parse();
  }

  /** Computes the display string for one cell (formula or passthrough). */
  displayCell(rowIndex: number, colIndex: number): string {
    const raw = this.rawCell(rowIndex, colIndex);
    if (!raw.trim().startsWith('=')) return raw; // passthrough unchanged
    try {
      return formatValue(this.resolveCell(rowIndex, colIndex));
    } catch (e) {
      return e instanceof CycleError ? CYCLE : ERR;
    }
  }
}

/** True when a cell's trimmed text is a formula (starts with '='). */
function isFormulaCell(text: string): boolean {
  return text.trim().startsWith('=');
}

/**
 * Computes a Markdown table into a display grid. Any cell whose trimmed text
 * starts with '=' is evaluated (recursively, with cycle detection); every other
 * cell passes through unchanged. When the table contains NO formula cells the
 * returned grid is identical to the input (and `hasFormula` is false), so
 * callers can render exactly as before.
 */
export function computeTableGrid(
  headerCells: string[],
  bodyRows: string[][],
): ComputedGrid {
  const hasFormula =
    headerCells.some(isFormulaCell) || bodyRows.some((row) => row.some(isFormulaCell));

  if (!hasFormula) {
    return { header: headerCells, body: bodyRows, hasFormula: false };
  }

  const evaluator = new TableEvaluator(headerCells, bodyRows);
  const header = headerCells.map((_, c) => evaluator.displayCell(0, c));
  const body = bodyRows.map((row, r) => row.map((_, c) => evaluator.displayCell(r + 1, c)));
  return { header, body, hasFormula: true };
}

/**
 * Convenience: evaluates a single formula string (e.g. `"=1+2*3"`) against an
 * optional table context, returning its display string. Handy for testing and
 * for one-off evaluation. A leading '=' is optional.
 */
export function computeFormula(
  formula: string,
  context: { header?: string[]; body?: string[][] } = {},
): string {
  const header = context.header ?? [];
  const body = context.body ?? [];
  const evaluator = new TableEvaluator(header, [...body, [formula]]);
  // The formula lives at the last body row, column A.
  return evaluator.displayCell(body.length + 1, 0);
}
